// src/pages/SearchHistoryPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
import axios from 'axios';
import './SearchHistoryPage.css';

const SearchHistoryPage = () => {
  const { user } = useAuth();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState({
    riskLevel: 'all',
    safetyResult: 'all',
    dateRange: 'all'
  });
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    fetchSearchHistory();
  }, [pagination.page, filters, sortBy]);

  const fetchSearchHistory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        sortBy,
        ...filters
      });

      const response = await axios.get(`/api/search-history?${params}`);
      
      setSearches(response.data.searches);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination
      }));
      setError('');
    } catch (err) {
      setError('Failed to load search history');
      console.error('Search history error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const clearFilters = () => {
    setFilters({
      riskLevel: 'all',
      safetyResult: 'all',
      dateRange: 'all'
    });
    setSortBy('newest');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'low': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'high': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getSafetyIcon = (safe) => {
    return safe ? '✅' : '⚠️';
  };

  const getQualityColor = (quality) => {
    switch (quality) {
      case 'good': return '#10b981';
      case 'fair': return '#f59e0b';
      case 'poor': return '#ef4444';
      default: return '#6b7280';
    }
  };

  if (loading && searches.length === 0) {
    return (
      <div className="search-history-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading your search history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="search-history-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Your Search History</h1>
          <p>View and analyze all your previous URL safety checks</p>
          <div className="stats-summary">
            <div className="stat-item">
              <span className="stat-number">{pagination.total}</span>
              <span className="stat-label">Total Searches</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {searches.filter(s => s.safetyResult).length}
              </span>
              <span className="stat-label">Safe URLs</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {searches.filter(s => !s.safetyResult).length}
              </span>
              <span className="stat-label">Flagged URLs</span>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        {/* Filters and Controls */}
        <div className="controls-section">
          <div className="filters">
            <div className="filter-group">
              <label htmlFor="riskLevel">Risk Level:</label>
              <select
                id="riskLevel"
                value={filters.riskLevel}
                onChange={(e) => handleFilterChange('riskLevel', e.target.value)}
              >
                <option value="all">All Risk Levels</option>
                <option value="low">Low Risk</option>
                <option value="medium">Medium Risk</option>
                <option value="high">High Risk</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="safetyResult">Safety Result:</label>
              <select
                id="safetyResult"
                value={filters.safetyResult}
                onChange={(e) => handleFilterChange('safetyResult', e.target.value)}
              >
                <option value="all">All Results</option>
                <option value="safe">Safe Only</option>
                <option value="unsafe">Flagged Only</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="sortBy">Sort By:</label>
              <select
                id="sortBy"
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="riskHigh">Highest Risk First</option>
                <option value="riskLow">Lowest Risk First</option>
              </select>
            </div>

            <button onClick={clearFilters} className="clear-filters-btn">
              Clear Filters
            </button>
          </div>
        </div>

        {/* Search Results */}
        {error && (
          <div className="error-state">
            <p>{error}</p>
            <button onClick={fetchSearchHistory} className="retry-button">
              Try Again
            </button>
          </div>
        )}

        {searches.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3>No searches found</h3>
            <p>Start by checking a job URL to build your search history!</p>
          </div>
        ) : (
          <>
            <div className="search-list">
              {searches.map((search) => (
                <div key={search.id} className="search-item">
                  <div className="search-header">
                    <div className="search-info">
                      <span className="safety-icon">
                        {getSafetyIcon(search.safetyResult)}
                      </span>
                      <div className="url-info">
                        <div className="url-text" title={search.originalUrl}>
                          {search.originalUrl.length > 80 
                            ? search.originalUrl.substring(0, 80) + '...'
                            : search.originalUrl
                          }
                        </div>
                        {search.companyName && (
                          <div className="company-name">
                            🏢 {search.companyName}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="search-meta">
                      <span 
                        className="risk-badge"
                        style={{ 
                          backgroundColor: getRiskColor(search.riskLevel),
                          color: 'white'
                        }}
                      >
                        {search.riskLevel?.toUpperCase() || 'UNKNOWN'}
                      </span>
                      <div className="search-date">
                        {formatDate(search.createdAt)}
                      </div>
                    </div>
                  </div>

                  <div className="search-details">
                    <div className="details-grid">
                      {search.jobDescriptionLines && (
                        <div className="detail-item">
                          <div className="detail-label">Job Description:</div>
                          <div className="detail-value">
                            {search.jobDescriptionLines} lines, {search.jobDescriptionWords} words
                            {search.jobDescriptionQuality && (
                              <span 
                                className="quality-badge"
                                style={{ color: getQualityColor(search.jobDescriptionQuality) }}
                              >
                                ({search.jobDescriptionQuality.toUpperCase()})
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {search.companyVerified !== null && (
                        <div className="detail-item">
                          <div className="detail-label">Company Verification:</div>
                          <div className={`detail-value ${search.companyVerified ? 'verified' : 'not-verified'}`}>
                            {search.companyVerified ? '✅ Verified' : '❌ Not Verified'}
                          </div>
                        </div>
                      )}

                      {search.finalUrl && search.finalUrl !== search.originalUrl && (
                        <div className="detail-item full-width">
                          <div className="detail-label">Final URL:</div>
                          <div className="detail-value final-url" title={search.finalUrl}>
                            {search.finalUrl.length > 100 
                              ? search.finalUrl.substring(0, 100) + '...'
                              : search.finalUrl
                            }
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="search-actions">
                      <button 
                        className="action-btn secondary"
                        onClick={() => {
                          console.log('Full search result:', search);
                          alert('Full search details logged to console (F12)');
                        }}
                      >
                        View Details
                      </button>
                      <button 
                        className="action-btn primary"
                        onClick={() => {
                          navigator.clipboard.writeText(search.originalUrl);
                          alert('URL copied to clipboard!');
                        }}
                      >
                        Copy URL
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="pagination-button"
                >
                  ← Previous
                </button>
                
                <div className="pagination-info">
                  <span className="page-numbers">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <span className="total-info">
                    ({pagination.total} total searches)
                  </span>
                </div>
                
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  className="pagination-button"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchHistoryPage;